/** /zh/use-cases/design-system-tracking — Chinese version. */
import { MarketingShell } from '../MarketingShell';

export function DesignSystemTrackingZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/use-cases/design-system-tracking"
      documentTitle="使用场景：设计系统追踪 · Draftlize"
      documentDescription="产品设计师怎么用 Draftlize 在多面之间保持设计系统一致。原则、token、组件理由作为卡片，agent 提建议前先读。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">使用场景 · 设计系统追踪</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            设计系统<br/>
            <em>记住它们的理由。</em>
          </h1>
          <p className="lede">
            给跨 3+ 产品面跑设计系统的产品设计师。颜色原则、字号 scale、组件理由作为卡片，agent 在提任何改动之前先读。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Figma 库 + Notion 原则文档<em>的问题。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">理由会蒸发</h3>
            <p>你按钮用了 sentence case。8 个月后你记不清为什么。新设计师建议 title case。没有理由记录，每个季度从零再决定一遍。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">跨面漂移</h3>
            <p>移动用 sentence case，web 用 title case，邮件模板用 ALL CAPS。没人发现直到品牌审计。每个面各自演化，没看其他面。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI 设计对话下个 sprint 就忘</h3>
            <p>你跟 Claude 说"链接用 semantic-blue 不用 brand-blue，因为可访问性对比"。3 周后新对话，Claude 又建议 brand-blue 链接。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">在 Draftlize 里<em>什么变了。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个原则是一张可寻址卡片</h3>
            <p><code>c-03 decision: 链接用 semantic-blue 不用 brand-blue</code> + <code>c-04 observation: brand-blue 在 warm-paper 背景下 WCAG AA 不过</code>作为 <code>→supports</code> 证据。两个永远可引。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">跨面矛盾以 gap 形式浮现</h3>
            <p>你写一个跟 web 原则冲突的移动原则。Draftlize 的 <code>conflict_detect</code> 标成 <code>gap_kind=conflict</code> 卡，两边同时显示，发版前。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">agent 在提建议前读原则</h3>
            <p>新对话："设计一个新的错误态。"agent 读 <code>c-03</code>（链接色）、<code>c-08</code>（sentence case 原则）、<code>c-15</code>（错误语调指南）作为 ambient context。建议出来就已经跟系统对齐。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">一个真实的日子，<em>压缩版。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">9:00</span>
            <h3 className="serif">扫新组件</h3>
            <p>打开 Draftlize，agent 浮现 3 张被标 stale 的卡（你昨天改了颜色原则）。5 分钟更新受影响的组件 spec。</p>
          </div>
          <div className="problem-col">
            <span className="num">11:30</span>
            <h3 className="serif">跨面审计</h3>
            <p>新 <code>gap_kind=conflict</code>：移动选了 sentence case，邮件模板提议 title case。决定哪个对（sentence case 赢），更新邮件原则卡。</p>
          </div>
          <div className="problem-col">
            <span className="num">15:00</span>
            <h3 className="serif">设计 review 准备</h3>
            <p>agent 读你所有设计原则卡，生成 `design-doc` 视图过滤到你今天 review 的组件。10 分钟 review，没有先例上的意外。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          "5 秒内捕到跨 3 个面的 sentence case 违规。以前每次设计 review 前要花 30 分钟手动检。"
        </blockquote>
        <cite>— Diego，金融科技 staff 产品设计师（Draftlize dogfood）</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">在<em>你的设计系统</em>上试。</h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            30-60 分钟把现有设计原则种进 Draftlize。第三次 review 时，agent 已经在不需要你重新解释的情况下把建议跟你系统对齐。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
